import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { MessageCircle, X, Send, Bot, AlertTriangle, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SESSION_KEY = "psytech_assistant_session";

const STARTER_QUESTIONS = [
  "What is PsyTech?",
  "How does the assessment work?",
  "Is PsyTech GDPR compliant?",
  "Can PsyTech diagnose me?",
  "How can I request a demo?",
];

const DISCLAIMER =
  "PsyTech Assistant provides general platform guidance and educational information only. It does not provide diagnosis, treatment, medication advice, or emergency support.";

function ChatMessage({ role, content }) {
  const isUser = role === "user";
  return (
    <div
      className={`flex w-full mb-3 ${isUser ? "justify-end" : "justify-start"}`}
      data-testid={isUser ? "assistant-msg-user" : "assistant-msg-bot"}
    >
      <div
        className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words shadow-sm ${
          isUser
            ? "bg-cyan-700 text-white rounded-br-md"
            : "bg-slate-100 text-slate-800 rounded-bl-md border border-slate-200"
        }`}
      >
        {content}
      </div>
    </div>
  );
}

export default function AssistantWidget() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sessionId, setSessionId] = useState(() => {
    try {
      return localStorage.getItem(SESSION_KEY) || null;
    } catch {
      return null;
    }
  });
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Persist sessionId
  useEffect(() => {
    if (sessionId) {
      try {
        localStorage.setItem(SESSION_KEY, sessionId);
      } catch {
        /* ignore */
      }
    }
  }, [sessionId]);

  // Load history when opened (if sessionId already exists)
  useEffect(() => {
    if (!open || !sessionId || messages.length > 0) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/assistant/session/${sessionId}/history`
        );
        if (!cancelled && data?.messages?.length) {
          setMessages(
            data.messages.map((m) => ({ role: m.role, content: m.content }))
          );
        }
      } catch {
        /* fresh session is fine */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, sessionId, messages.length]);

  // Auto-scroll on new messages / loading
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading, open]);

  // Focus input when opened
  useEffect(() => {
    if (open && inputRef.current) {
      const t = setTimeout(() => inputRef.current?.focus(), 200);
      return () => clearTimeout(t);
    }
  }, [open]);

  const send = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || loading) return;
    setError("");
    setMessages((prev) => [...prev, { role: "user", content: trimmed }]);
    setInput("");
    setLoading(true);
    if (window.__psyTrack) window.__psyTrack("assistant_message_sent");
    try {
      const { data } = await axios.post(`${API}/assistant/chat`, {
        session_id: sessionId,
        message: trimmed,
        language: i18n.language || "en",
      });
      if (data?.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
      }
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.reply },
      ]);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429) {
        setError(detail || "You're sending messages too fast. Please wait a moment.");
      } else {
        setError(
          detail ||
            "The assistant is having trouble right now. Please try again in a moment."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const showStarters = useMemo(() => messages.length === 0, [messages.length]);

  // Hide widget on admin pages
  if (location.pathname.startsWith("/admin")) return null;

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          type="button"
          onClick={() => {
            setOpen(true);
            if (window.__psyTrack) window.__psyTrack("assistant_opened");
          }}
          aria-label="Open PsyTech Assistant"
          data-testid="assistant-launcher"
          className="fixed bottom-5 right-5 md:bottom-6 md:right-6 z-[60] group flex items-center gap-2 rounded-full bg-cyan-700 hover:bg-cyan-800 text-white shadow-xl shadow-cyan-900/30 px-4 py-3 transition-all hover:scale-105 active:scale-95"
        >
          <MessageCircle className="w-5 h-5" />
          <span className="hidden sm:inline text-sm font-semibold pr-1">
            Ask PsyTech
          </span>
          <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-cyan-300 ring-2 ring-white animate-pulse" />
        </button>
      )}

      {/* Chat window */}
      {open && (
        <div
          data-testid="assistant-window"
          className="fixed z-[60] bottom-0 right-0 left-0 sm:left-auto sm:bottom-6 sm:right-6
                     w-full sm:w-[380px] md:w-[400px] h-[85vh] sm:h-[600px] sm:max-h-[80vh]
                     bg-white sm:rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden
                     animate-fade-in"
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-3 px-4 py-3 bg-gradient-to-r from-slate-900 to-cyan-900 text-white">
            <div className="flex items-center gap-3 min-w-0">
              <div className="p-2 rounded-full bg-cyan-600/30 ring-1 ring-cyan-400/40 shrink-0">
                <Bot className="w-5 h-5 text-cyan-200" />
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-sm leading-tight">
                  PsyTech Assistant
                </div>
                <div className="text-[11px] text-cyan-200/90 truncate">
                  Platform guidance and assessment support
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close PsyTech Assistant"
              data-testid="assistant-close-btn"
              className="p-1.5 rounded-full hover:bg-white/10 transition-colors shrink-0"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Disclaimer */}
          <div
            className="px-4 py-2.5 text-[11px] leading-snug text-amber-900 bg-amber-50 border-b border-amber-100 flex gap-2"
            data-testid="assistant-disclaimer"
          >
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-600" />
            <span>{DISCLAIMER}</span>
          </div>

          {/* Messages area */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-4 bg-slate-50"
            data-testid="assistant-messages"
          >
            {showStarters && (
              <div className="mb-4">
                <div className="text-xs text-slate-500 mb-2">
                  Hi! I can help you understand PsyTech. Try one of these:
                </div>
                <div className="flex flex-col gap-2">
                  {STARTER_QUESTIONS.map((q) => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => send(q)}
                      data-testid={`assistant-starter-${q.slice(0, 20).replace(/\s+/g, "-").toLowerCase()}`}
                      className="text-left text-sm px-3 py-2 rounded-lg bg-white border border-slate-200 hover:border-cyan-500 hover:text-cyan-800 hover:bg-cyan-50/40 transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, idx) => (
              <ChatMessage key={idx} role={m.role} content={m.content} />
            ))}

            {loading && (
              <div className="flex items-center gap-2 text-slate-500 text-xs pl-1" data-testid="assistant-loading">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Thinking…
              </div>
            )}

            {error && (
              <div
                className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
                data-testid="assistant-error"
              >
                {error}
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-slate-200 bg-white p-3">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Ask about PsyTech…"
                rows={1}
                data-testid="assistant-input"
                className="flex-1 resize-none min-h-[40px] max-h-32 rounded-xl border border-slate-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 outline-none px-3 py-2 text-sm bg-slate-50"
              />
              <button
                type="button"
                onClick={() => send()}
                disabled={loading || !input.trim()}
                data-testid="assistant-send-btn"
                className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl bg-cyan-700 text-white hover:bg-cyan-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                aria-label="Send message"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <div className="text-[10px] text-slate-400 mt-1.5 px-1">
              Powered by PsyTech AI · For informational use only
            </div>
          </div>
        </div>
      )}
    </>
  );
}
